import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Tag, Spin, message, Input, Select, DatePicker, Tooltip } from 'antd';
import { Edit } from "lucide-react";
import { SearchOutlined } from "@ant-design/icons";
import { getRequest } from '../../../../service';
import AttendanceLogRequest from '../../../../modals/attendanceLogReq';
import { getEmployeeData } from '../../../../utils/localStorage';
import { formatDate } from '../../../../utils/dateTime';
import ModalWrapper from "../../../../Modal";

const { RangePicker } = DatePicker;

const AttendanceRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateRange, setDateRange] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    
    const employeeData = getEmployeeData();

    // Get user role and permissions
    const role = employeeData.role;
    const attendanceRequestPermissions = employeeData?.designation?.permissions?.Attendance?.['Attendance Request'] || [];
    
    const canView = role === "companyadmin" || attendanceRequestPermissions.includes("view");
    const canEdit = role === "companyadmin" || attendanceRequestPermissions.includes("edit");

    // Status filter options
    const statusOptions = [
        { value: "all", label: "All" },
        { value: "pending", label: "Pending" },
        { value: "approved", label: "Approved" },
        { value: "rejected", label: "Rejected" }
    ];

    // Debounced search function
    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (searchTerm) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    setPagination(prev => ({ ...prev, current: 1 }));
                    fetchRequests(1, pagination.pageSize, searchTerm, statusFilter, dateRange);
                }, 500); // 500ms delay
            };
        })(),
        [pagination.pageSize, statusFilter, dateRange]
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSearch(value);
    };

    // Handle status filter change
    const handleStatusChange = (value) => {
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchRequests(1, pagination.pageSize, search, value, dateRange);
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchRequests(1, pagination.pageSize, search, statusFilter, dates);
    };

    useEffect(() => {
        fetchRequests();
    }, [canView]);

    const fetchRequests = async (page = 1, pageSize = 10, searchTerm = search, statusTerm = statusFilter, dateRangeTerm = dateRange) => {
        setLoading(true);
        try {
            if (!canView) {
                setRequests([]);
                setPagination(prev => ({ ...prev, total: 0 }));
                return;
            }

            // Build query parameters
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: pageSize.toString()
            });

            // Add search parameter if provided
            if (searchTerm && searchTerm.trim()) {
                queryParams.append('search', searchTerm.trim());
            }

            // Add status parameter if not "all"
            if (statusTerm && statusTerm !== "all") {
                queryParams.append('status', statusTerm);
            }

            // Add date range parameters if provided
            if (dateRangeTerm && dateRangeTerm.length === 2) {
                const startDate = dateRangeTerm[0].format('YYYY-MM-DD');
                const endDate = dateRangeTerm[1].format('YYYY-MM-DD');
                queryParams.append('startDate', startDate);
                queryParams.append('endDate', endDate);
            }
            let response;
            if(role === "companyadmin"){
                response = await getRequest(`/api/requestshift?${queryParams.toString()}`);
            }else{
                queryParams.append('employeeId', employeeData?.userId);
                response = await getRequest(`/api/requestshift?managerId=${employeeData?.userId}&includeTeamLeads=true&${queryParams.toString()}`);
            }
            const res = response;
            const requestData = res?.requestShifts || [];
            
            // Handle pagination from API response
            const apiPagination = res?.pagination || {};
            const totalCount = apiPagination.totalItems || 0;
            const currentPage = apiPagination.currentPage || page;
            const itemsPerPage = apiPagination.itemsPerPage || pageSize;

            setRequests(requestData);
            setPagination(prev => ({
                ...prev,
                current: currentPage,
                pageSize: itemsPerPage,
                total: totalCount
            }));
        } catch (err) {
            setRequests([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (paginationInfo, filters, sorter) => {
        // Update pagination state
        setPagination(prev => ({
            ...prev,
            current: paginationInfo.current || 1,
            pageSize: paginationInfo.pageSize || 10
        }));
        
        // Fetch data with new pagination
        fetchRequests(
            paginationInfo.current || 1, 
            paginationInfo.pageSize || 10, 
            search, 
            statusFilter, 
            dateRange
        );
    };

    const getStatusColor = (status) => {
        switch((status || '').toLowerCase()) {
            case 'approved':
                return 'success';
            case 'pending':
                return 'warning';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const handleEditRequest = (record) => {
        if (!canEdit) {
            message.warning("You don't have permission to edit attendance requests.");
            return;
        }
        
        // Check if status is approved
        if (record.status?.toLowerCase() === 'approved') {
            message.warning("Cannot edit approved attendance requests.");
            return;
        }
        
        setSelectedRequest(record);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRequest(null);
        fetchRequests()
    };

    // Columns without Actions
    const baseColumns = [
        {
            title: 'Employee Name',
            dataIndex: 'employee',
            key: 'employeeName',
            render: (employee) => {
                if (!employee) return '-';
                const firstName = employee.firstName || '';
                const lastName = employee.lastName || '';
                const fullName = `${firstName} ${lastName}`.trim();
                return fullName || '-';
            }
        },
        {
            title: 'Request Date',
            dataIndex: 'requestEditShiftDate',
            key: 'requestDate',
            render: (date) => date ? formatDate(date) : '-',
        },
        {
            title: 'Change Start Time',
            dataIndex: 'changeStartTime',
            key: 'changeStartTime',
            render: (time) => time ? time : '-',
        },
        {
            title: 'Change End Time',
            dataIndex: 'changeEndTime',
            key: 'changeEndTime',
            render: (time) => time ? time : '-',
        },
        {
            title: 'Original Check In',
            dataIndex: ['attendanceLog', 'checkIn'],
            key: 'originalCheckIn',
            render: (checkIn, record) => {
                if (!checkIn || checkIn === '-' || checkIn === 'ss') return '-';
                return (
                    <Tooltip title={`Original: ${checkIn}`}>
                        <span>{checkIn.slice(0,5)}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Original Check Out',
            dataIndex: ['attendanceLog', 'checkOut'],
            key: 'originalCheckOut',
            render: (checkOut, record) => {
                if (!checkOut || checkOut === '-' || checkOut === 'ss') return '-';
                return (
                    <Tooltip title={`Original: ${checkOut}`}>
                        <span>{checkOut.slice(0,5)}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Attendance Status',
            dataIndex: ['attendanceLog', 'status'],
            key: 'attendanceStatus',
            render: (status) => {
                if (!status) return '-';
                return (
                    <Tag color={status === 'Missing' ? 'error' : status === 'Late' ? 'warning' : 'default'}>
                        {status}
                    </Tag>
                );
            },
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            render: (reason) => reason || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status}
                </Tag>
                ),
        },
    ];

    // Always show Actions column with Edit icon
    const columns = [
        ...baseColumns,
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const isApproved = record.status?.toLowerCase() === 'approved';
                const isDisabled = !canEdit || isApproved;
                
                return (
                    <Space>
                        <Button 
                            type="text" 
                            icon={<Edit className="w-4 h-4" />} 
                            onClick={() => handleEditRequest(record)}
                            className={!isDisabled ? "text-blue-600 hover:text-blue-800" : "text-gray-400 cursor-not-allowed"}
                            disabled={isDisabled}
                            title={!isDisabled ? "Edit" : isApproved ? "Cannot edit approved requests" : "You don't have permission to edit"}
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <>
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-semibold">Attendance Requests</h1>
            </div>

            {/* Search + Filters */}
            <div className="w-full flex flex-col gap-3 mb-6">
                <div className="flex items-center gap-3 flex-1">
                    <div className="relative flex-1">
                        <Input
                            placeholder="Search by employee name"
                            value={search}
                            onChange={handleSearchChange}
                            prefix={<SearchOutlined className="text-gray-500" />}
                            className="px-4 py-3 rounded-lg h-10"
                        />
                    </div>
                    <Select
                        value={statusFilter}
                        onChange={handleStatusChange}
                        options={statusOptions}
                        className="min-w-[120px]"
                        placeholder="Status"
                    />
                    <RangePicker
                        onChange={handleDateRangeChange}
                        placeholder={['Start Date', 'End Date']}
                        className="min-w-[240px]"
                        format="YYYY-MM-DD"
                    />
                    {/* <Button
                        onClick={() => {
                            setSearch("");
                            setStatusFilter("all");
                            setDateRange(null);
                            setPagination(prev => ({ ...prev, current: 1 }));
                            fetchRequests(1, pagination.pageSize, "", "all", null);
                        }}
                        className="text-gray-600 hover:text-gray-800"
                    >
                        Reset
                    </Button> */}
                </div>
            </div>
            <Spin spinning={loading} size="large">
                <Table 
                    columns={columns} 
                    dataSource={canView ? requests : []}
                    rowKey="_id"
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        total: canView ? pagination.total : 0,
                        pageSize: pagination.pageSize,
                        current: pagination.current,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        position: ['bottomCenter']
                    }}
                    onChange={handleTableChange}
                />
                {!canView && !loading && (
                    <div className="text-center text-gray-500 py-4">You don't have permission to view attendance requests.</div>
                )}
            </Spin>
        </div>
        <ModalWrapper
            open={isModalOpen}
            onRequestClose={handleCloseModal}
            title="Attendance Request Details"
        >
            <AttendanceLogRequest 
                onClose={handleCloseModal} 
                requestData={selectedRequest}
            />
        </ModalWrapper>   
        </>
    );
}

export default AttendanceRequests;
